import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import PageMeta from "../components/PageMeta";
import API_URL from "../config";

export default function VerifyCertificate() {
  const [searchParams] = useSearchParams();
  const [certId, setCertId] = useState(searchParams.get("cert_id") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  // Auto-verify if cert_id is in URL params
  useEffect(() => {
    const urlCertId = searchParams.get("cert_id");
    if (urlCertId) {
      setCertId(urlCertId);
      // Small delay to ensure state is set
      const timer = setTimeout(() => {
        handleVerify(urlCertId);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleVerify = async (id) => {
    // id might be a string from useEffect call or an Event from onClick
    const certToVerify = String(typeof id === 'string' ? id : certId).trim();
    if (!certToVerify) {
      setError("Please enter a Certificate ID");
      return;
    }

    setLoading(true);
    setError("");
    setData(null);

    try {
      const response = await fetch(
        `${API_URL}/verify/${certToVerify}`
      );
      const result = await response.json();

      if (result.success && result.cert_id) {
        setData(result);
      } else if (result.error) {
        setError(result.error);
      } else {
        setError("Certificate not valid or not found");
      }
    } catch {
      setError("Unable to verify certificate. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-50">
      <div className="max-w-4xl mx-auto">
        {/* Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h1 className="text-2xl font-semibold text-slate-800 mb-1">
            Certificate Verification
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Enter the certificate ID to verify its authenticity
          </p>

          {/* Input */}
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Enter Certificate ID"
              value={certId}
              onChange={(e) => setCertId(e.target.value)}
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-300
                focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
            <button
              onClick={handleVerify}
              disabled={loading}
              className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl
                hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {loading ? "Verifying..." : "Verify"}
            </button>
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-600 text-sm mt-4">{error}</p>
          )}
        </div>

        {/* RESULT */}
        {data && (
          <>
            <PageMeta
              title={`Certificate ${data.cert_id} | SmartGoNext`}
              description={`Verified certificate for ${data.student_name}. Internship: ${data.details.internship_from} → ${data.details.internship_to}.`}
              url={window.location.href}
            />

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-green-600 text-lg">✔</span>
              <h2 className="text-lg font-semibold text-slate-800">
                Certificate Verified
              </h2>
            </div>

            {/* DETAILS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <p><span className="text-slate-500">Name:</span> {data.student_name}</p>
              <p><span className="text-slate-500">Email:</span> {data.details.email}</p>
              <p><span className="text-slate-500">Phone:</span> {data.details.phone}</p>
              <p><span className="text-slate-500">College:</span> {data.details.college}</p>
              <p><span className="text-slate-500">Department:</span> {data.details.department}</p>
              <p><span className="text-slate-500">DOB:</span> {data.details.dob}</p>
              <p><span className="text-slate-500">Age:</span> {data.details.age}</p>
              <p className="sm:col-span-2">
                <span className="text-slate-500">Internship:</span>{" "}
                {data.details.internship_from} → {data.details.internship_to}
              </p>
              <p className="sm:col-span-2">
                <span className="text-slate-500">Address:</span>{" "}
                {data.details.address}
              </p>
            </div>

            {/* IMAGES */}
            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">
                  Student Photo
                </p>
                <img
                  src={data.student_photo || data.student_photo_url}
                  alt="Student"
                  className="rounded-xl border border-slate-200"
                />
              </div>

              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">
                  Certificate
                </p>
                <img
                  src={data.cert_photo || data.cert_photo_url}
                  alt="Certificate"
                  className="rounded-xl border border-slate-200"
                />
              </div>
            </div>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
